<template>
  <div>
    <h2 class="h2">Billing account</h2>
    <h3 class="h5 sub-title mb-4 text-muted">Manage billing details</h3>

    <template v-if="!edit">
      <c-button color="primary" class="mb-4" @click="editForm">Edit settings</c-button>

      <template v-if="organisation">
        <table class="table">
          <tr>
            <td>Bill to name</td>
            <td>
              <span v-if="form.billTo">{{ form.billTo }}</span>
              <span v-else class="not-set">Not set</span>
            </td>
            <td>
              <p v-html="fieldDescriptions.billTo" />
            </td>
          </tr>
          <tr>
            <td>Bill to email</td>
            <td>
              <span v-if="form.email">{{ form.email }}</span>
              <span v-else class="not-set">Not set</span>
            </td>
            <td>
              <p v-html="fieldDescriptions.email" />
            </td>
          </tr>
          <tr>
            <td>Phone number</td>
            <td>
              <span v-if="form.phoneNumber">{{ form.phoneNumber }}</span>
              <span v-else class="not-set">Not set</span>
            </td>
            <td>
              <p v-html="fieldDescriptions.phoneNumber" />
            </td>
          </tr>
          <tr>
            <td>Country</td>
            <td>
              <span v-if="form.country">
                {{ countries[form.country].name }}
              </span>
              <span v-else class="not-set">Not set</span>
            </td>
            <td>
              <p v-html="fieldDescriptions.country" />
            </td>
          </tr>
        </table>
      </template>
      <template v-else>
        <p>No billing information has been entered.</p>
      </template>
    </template>

    <template v-if="edit">
      <table class="table">
        <tr>
          <td>Bill to name</td>
          <td>
            <c-input
              v-model="form.billTo"
              type="text"
              :lazy="false"
              :is-valid="checkIfValid('billTo')"
              placeholder="Michael Scott"
              invalid-feedback="Please enter a name"
            />
          </td>
          <td>
            <p v-html="fieldDescriptions.billTo" />
          </td>
        </tr>
        <tr>
          <td>Email</td>
          <td>
            <c-input
              v-model="form.email"
              type="text"
              :lazy="false"
              :is-valid="checkIfValid('email')"
              placeholder="michael@dundermifflin.com"
              invalid-feedback="Please enter a valid email"
            />
          </td>
          <td>
            <p v-html="fieldDescriptions.email" />
          </td>
        </tr>
        <tr>
          <td>Phone number</td>
          <td>
            <c-input
              v-model="form.phoneNumber"
              type="text"
              :lazy="false"
              :is-valid="checkIfValid('phoneNumber')"
              placeholder="Your phone number"
              invalid-feedback="Please enter a phone number"
            />
          </td>
          <td>
            <p v-html="fieldDescriptions.phoneNumber" />
          </td>
        </tr>
        <tr>
          <td>Country</td>
          <td>
            <span class="select">
              <c-select
                  :value="form.country"
                  placeholder="Select country"
                  :options="countryList"
                  :lazy="false"
                  :is-valid="checkIfValid('country')"
                  invalid-feedback="Please select a country"
                  @update:value="handleCountryUpdate"
              />
            </span>
          </td>
          <td>
            <p v-html="fieldDescriptions.country" />
          </td>
        </tr>
      </table>

      <c-button-group class="mb-5">
        <c-button color="primary" @click="save">
          <c-spinner v-if="isSaving" color="success" size="sm" />
          <span v-else>Update</span>
        </c-button>
        <c-button color="secondary" :disabled="isSaving" @click="cancelEdit">Cancel</c-button>
      </c-button-group>
    </template>

    <h2 class="h2">Payment method</h2>

    <template v-if="billingMinimumRequirements()">
      <card :organisation="organisation" />
    </template>

    <template v-else>
      <h2 class="subtitle" style="margin-top: 20px">
        Billing details must be completed before a payment method can be added.
      </h2>
    </template>
  </div>
</template>

<script>
import { countries } from 'countries-list';
import _ from 'lodash';
import { validationMixin } from 'vuelidate';
import {
  required, email,
} from 'vuelidate/lib/validators';
import { buildEndpoint } from '../../../libs/buildEndPoint';
import Card from './card.vue';

export default {
  mixins: [validationMixin],
  components: {
    Card,
  },
  props: {
    organisation: Object,
  },
  data() {
    return {
      billing: null,
      edit: false,
      countries,
      countryList: [],
      isSaving: false,
      form: {
        billTo: null,
        email: null,
        phoneNumber: null,
        country: null,
      },
      fieldDescriptions: {
        billTo: 'The name of whom we will make the invoice out to',
        email: 'The email address we will send invoice notifications to',
        phoneNumber: "The phone number we will call if we can't reach you via email",
        country: 'The country of the credit card used ',
      },
    };
  },
  computed: {
    isValid() {
      return !this.$v.form.$invalid;
    },
    isDirty() {
      return this.$v.form.$anyDirty;
    },
  },
  validations: {
    form: {
      billTo: {
        required,
      },
      phoneNumber: {
        required,
      },
      country: {
        required,
      },
      email: {
        required,
        email,
      },
    },
  },

  created() {
    this.setData();
    this.getCountries();
  },

  methods: {
    checkIfValid(fieldName) {
      const field = this.$v.form[fieldName];
      if (!field.$dirty) {
        return null;
      }
      return !(field.$invalid || field.$model === '');
    },
    handleCountryUpdate(data) {
      this.form.country = data;
    },
    getCountries() {
      const countryList = [];
      Object.keys(countries).forEach((key) => {
        countryList.push({
          value: key,
          label: countries[key].name,
        });
      });
      this.countryList = countryList;
    },
    billingMinimumRequirements() {
      return (
        _.get(this.organisation, 'config.billing.billTo')
        && _.get(this.organisation, 'config.billing.email')
        && _.get(this.organisation, 'config.billing.phoneNumber')
        && _.get(this.organisation, 'config.billing.country')
      );
    },
    setData() {
      if (_.has(this.organisation, 'config.billing')) {
        this.billing = this.organisation.config.billing;
      }
      this.form.billTo = _.get(this.organisation, 'config.billing.billTo', '');
      this.form.email = _.get(this.organisation, 'config.billing.email', '');
      this.form.phoneNumber = _.get(this.organisation, 'config.billing.phoneNumber', '');
      this.form.country = _.get(this.organisation, 'config.billing.country', '');
    },
    editForm() {
      this.edit = true;
      this.$v.$reset();
    },
    cancelEdit() {
      this.$v.$reset();
      this.edit = false;
      this.setData();
    },
    save() {
      this.$v.$touch();

      if (!this.isValid) {
        return;
      }

      this.isSaving = true;

      const data = {
        // eslint-disable-next-line no-underscore-dangle
        id: this.organisation._id,
        billTo: this.form.billTo,
        email: this.form.email,
        phoneNumber: this.form.phoneNumber,
        country: this.form.country,
      };

      this.$http
        .post(buildEndpoint('organisation-admin/organisation/billing/update'), data)
        .then((response) => {
          this.isSaving = false;
          if (response.data.success) {
            this.$v.$reset();
            this.edit = false;
            this.$store.commit('addSuccessMessage', {
              header: 'Success',
              message: 'Billing details updated',
            });
          } else {
            this.$store.commit('addErrorMessage', {
              header: 'Error',
              message: 'Error saving billing details',
            });
          }
        })
        .catch(() => {
          this.isSaving = false;
          this.$store.commit('addErrorMessage', {
            header: 'Error',
            message: 'Error saving billing details',
          });
        });
    },
  },
};
</script>

<style lang="scss" scoped>
table {
  tr {
    td {
      &:first-child {
        font-weight: bold;
        width: 200px;
      }
      &:last-child {
        font-style: italic;
      }
      .not-set {
        color: silver;
      }
    }
  }
}
.buttons {
  margin-bottom: 20px;
}
.info {
  font-weight: normal;
  font-style: italic;
  margin-bottom: 20px;
}
</style>
